import type { CardDto, ColumnDto } from '@kanban/shared';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useState } from 'react';
import { DropIndicator } from './dnd/DropIndicator.js';
import { SortableCard } from './dnd/SortableCard.js';

/**
 * Eine Spalte. Auf dem Smartphone fuellt sie ~88 % der Viewport-Breite, damit
 * die Nachbarspalte angeschnitten sichtbar bleibt — der visuelle Hinweis,
 * dass sich hier wischen laesst (SPEC 8.1).
 */
export function ColumnView({
  column,
  onOpenCard,
  onAddCard,
  onOpenSettings,
  activeCardId = null,
  watchers,
  dropIndex = null,
  dropHeight = 72,
  onCompleteCard,
}: {
  column: ColumnDto;
  onOpenCard: (card: CardDto) => void;
  onAddCard: (columnId: string, title: string) => void;
  onOpenSettings?: () => void;
  /** Karte, die gerade gezogen wird — bleibt als blasser Ghost stehen. */
  activeCardId?: string | null;
  /** Karten-ID -> Name dessen, der sie gerade offen hat (SPEC 7.3). */
  watchers?: Map<string, string>;
  /** Index, an dem der Platzhalter erscheint, oder null. */
  dropIndex?: number | null;
  dropHeight?: number;
  /** Schnell erledigen, ohne das Kartendetail zu oeffnen. Weggelassen = kein Knopf. */
  onCompleteCard?: (card: CardDto) => void;
}) {
  const [draft, setDraft] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Damit auch eine leere Spalte ein gueltiges Ziel ist.
  const { setNodeRef } = useDroppable({ id: column.id, data: { type: 'column' } });

  const overLimit = column.wipLimit !== null && column.cards.length > column.wipLimit;

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const title = draft.trim();
    if (!title) return;
    onAddCard(column.id, title);
    setDraft('');
  }

  /**
   * Anzeigeliste aufbauen. Zwei Dinge passieren gleichzeitig (SPEC 8.2):
   *   - die gezogene Karte bleibt als Ghost an ihrer urspruenglichen Stelle
   *     stehen und zaehlt fuer die Zielposition NICHT mit,
   *   - an `dropIndex` der Liste ohne sie wird der Platzhalter eingeschoben.
   */
  const entries: (CardDto | 'indicator')[] = [];
  let otherIndex = 0;
  for (const card of column.cards) {
    if (card.id === activeCardId) {
      entries.push(card);
      continue;
    }
    if (dropIndex === otherIndex) entries.push('indicator');
    entries.push(card);
    otherIndex += 1;
  }
  // Platzhalter am Ende der Spalte.
  if (dropIndex === otherIndex) entries.push('indicator');

  return (
    <section
      ref={setNodeRef}
      className="board-column flex max-h-full flex-col rounded-[var(--radius-lg)] bg-surface-muted"
      data-testid="column"
      data-column-id={column.id}
      aria-label={column.title}
    >
      {/* Sticky Spaltenkopf mit Kartenzaehler (SPEC 8.1). */}
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-[var(--radius-lg)] bg-surface-muted px-3 py-2">
        {onOpenSettings ? (
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid="column-menu"
            className="min-w-0 truncate text-left text-sm font-semibold"
            title="Spalten-Einstellungen"
          >
            {column.title}
          </button>
        ) : (
          <h2 className="truncate text-sm font-semibold">{column.title}</h2>
        )}
        <span
          className={`rounded-full px-2 py-0.5 text-xs ${overLimit ? 'bg-warning-soft text-warning' : 'text-muted'}`}
          title={
            column.wipLimit
              ? `${column.cards.length} von maximal ${column.wipLimit}`
              : `${column.cards.length} Karten`
          }
        >
          {column.cards.length}
          {column.wipLimit !== null ? ` / ${column.wipLimit}` : ''}
        </span>

        {/*
          Sichtbarer Einstieg zusaetzlich zum Titel. Der Titel bleibt
          klickbar, aber ein Titel, der heimlich ein Button ist, wird nicht
          gefunden — genau daran ist das Entfernen einer Spalte bisher
          gescheitert.
        */}
        {onOpenSettings && (
          <button
            type="button"
            onClick={onOpenSettings}
            data-testid="column-menu-button"
            aria-label={`Einstellungen der Spalte ${column.title}`}
            title="Spalten-Einstellungen"
            className="-mr-1 shrink-0 rounded-[var(--radius-sm)] text-muted"
            style={{ minWidth: '2rem', minHeight: '2rem' }}
          >
            <span aria-hidden="true">⋯</span>
          </button>
        )}
      </header>

      {overLimit && (
        <p className="mx-3 mb-2 rounded-[var(--radius-sm)] bg-warning-soft px-2 py-1 text-xs text-warning">
          WIP-Limit überschritten
        </p>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        <SortableContext
          items={column.cards.map((card) => card.id)}
          strategy={verticalListSortingStrategy}
        >
          {entries.map((entry, index) =>
            entry === 'indicator' ? (
              <DropIndicator key={`indicator-${index}`} height={dropHeight} />
            ) : (
              <SortableCard
                key={entry.id}
                card={entry}
                isDragging={entry.id === activeCardId}
                onOpen={onOpenCard}
                watcherName={watchers?.get(entry.id) ?? null}
                onComplete={onCompleteCard}
              />
            ),
          )}
        </SortableContext>

        {column.cards.length === 0 && dropIndex === null && !isAdding && (
          <p className="py-6 text-center text-xs text-muted">Keine Karten</p>
        )}

        {isAdding ? (
          <form onSubmit={submit} className="space-y-2">
            <textarea
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submit(event);
                }
                if (event.key === 'Escape') setIsAdding(false);
              }}
              rows={2}
              placeholder="Titel der Karte"
              aria-label="Titel der neuen Karte"
              className="w-full resize-none rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                className="flex-1 rounded-[var(--radius)] bg-accent px-3 text-sm font-medium text-[var(--color-accent-contrast)]"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                Hinzufügen
              </button>
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="rounded-[var(--radius)] border border-border bg-surface px-3 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                Abbrechen
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            data-testid="add-card"
            className="w-full rounded-[var(--radius)] border border-dashed border-border-strong px-3 text-sm text-muted"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            + Karte
          </button>
        )}
      </div>
    </section>
  );
}
